import { extendTailwindMerge } from 'tailwind-merge'

import { radius } from '../tokens/radius'
import { spacing } from '../tokens/spacing'
import { transparent } from '../tokens/transparent'

/**
 * The scale steps `tailwind-preset.js` emits, as tailwind-merge needs to see them.
 *
 * Figma's spacing aliases carry a density prefix whose only job is to group them in
 * the panel, and the preset drops it — `medium-less-dense/m` becomes `p-m`. The same
 * slice is repeated here rather than imported, because the preset is CommonJS that
 * would otherwise be pulled into the React Native bundle. `merge.spec.ts` compares
 * both lists against the preset's own scales, so they cannot drift apart silently.
 */
export const SPACING_STEPS = Object.keys(spacing).map(alias => alias.slice(alias.indexOf('/') + 1))
export const RADIUS_STEPS = Object.keys(radius)
export const OPACITY_STEPS = Object.keys(transparent)

/**
 * Bare `outline`, which sets the outline *style*, in a group of its own.
 *
 * tailwind-merge files it with the outline *widths*, so `outline outline-2` returns
 * only `outline-2` — the width survives, the style is never set, and in CSS that is
 * no outline at all. It is how the keyboard focus ring on every universal Button was
 * dead once already: the ring declares all four utilities and the merge drops the one
 * that matters before the class reaches the DOM.
 *
 * It belongs here rather than one layer up because this is the merge the Button runs,
 * and the ring arrives as part of the `className` a caller hands it.
 *
 * A separate group rather than an override of `outline-w`: overriding that loses
 * arbitrary widths, so `outline-2 outline-[3px]` stops collapsing.
 */
export const OUTLINE_STYLE_CLASSES = ['outline']

/**
 * tailwind-merge does not know a scale it was not told about.
 *
 * Every aos-ds contract class names a Figma token — `px-m`, `gap-xs`, `rounded-common`,
 * `opacity-disabled` — and to stock tailwind-merge those read as unknown classes, not
 * as padding, gap, radius or opacity. So `twMerge('px-m', 'p-0')` returns BOTH, and
 * the cascade decides: `px-m` wins, and a call site asking for `p-0` gets 12px anyway.
 *
 * That is not a tidiness problem. It silently un-does every override a call site makes
 * on a contract class, which is how an icon-only button ends up a pill — the override
 * is right there in the source, so the bug is invisible in review and in the diff.
 *
 * Listing the steps puts them in the groups they belong to, and the built-in conflict
 * map does the rest (`p` beats `px`, `rounded` beats `rounded-t`, and so on).
 */
export const AOS_DS_MERGE_CONFIG = {
    extend: {
        classGroups: {
            'p': [{ p: SPACING_STEPS }],
            'px': [{ px: SPACING_STEPS }],
            'py': [{ py: SPACING_STEPS }],
            'pt': [{ pt: SPACING_STEPS }],
            'pr': [{ pr: SPACING_STEPS }],
            'pb': [{ pb: SPACING_STEPS }],
            'pl': [{ pl: SPACING_STEPS }],
            'm': [{ m: SPACING_STEPS }],
            'mx': [{ mx: SPACING_STEPS }],
            'my': [{ my: SPACING_STEPS }],
            'gap': [{ gap: SPACING_STEPS }],
            'gap-x': [{ 'gap-x': SPACING_STEPS }],
            'gap-y': [{ 'gap-y': SPACING_STEPS }],
            'size': [{ size: SPACING_STEPS }],
            'w': [{ w: SPACING_STEPS }],
            'h': [{ h: SPACING_STEPS }],
            'rounded': [{ rounded: RADIUS_STEPS }],
            'opacity': [{ opacity: OPACITY_STEPS }],
            'outline-style-keyword': OUTLINE_STYLE_CLASSES,
        },
    },
}

/**
 * `twMerge` that understands the aos-ds scale. Import this inside `aos-ds`, never
 * `tailwind-merge` directly, or a call-site override stops winning.
 */
export const twMerge = extendTailwindMerge(AOS_DS_MERGE_CONFIG)
